import { useEffect, useState, type DependencyList } from 'react';
import type * as Y from 'yjs';

/**
 * Subscribe to a Yjs source and project it into a React-renderable snapshot.
 *
 * - `derive` runs once for the initial state and again every time `subscribe`
 *   reports a change. Keep it pure and reasonably fast.
 * - `subscribe(refresh)` should attach observers to whatever Yjs structures
 *   feed the snapshot and return an `unsubscribe` cleanup.
 * - `deps` is the React dep list — the effect re-binds when any dep changes,
 *   matching the closure shape of `derive` and `subscribe`.
 *
 * Replaces three near-identical `useState(derive)` + `useEffect(observe)`
 * patterns in useNodesFromYjs / useEdgesFromYjs / useFormattingState.
 */
export function useYjsSnapshot<T>(
  derive: () => T,
  subscribe: (refresh: () => void) => () => void,
  deps: DependencyList,
): T {
  const [snapshot, setSnapshot] = useState<T>(derive);

  useEffect(() => {
    const refresh = () => setSnapshot(derive());
    const unsubscribe = subscribe(refresh);
    // Re-derive once on mount/dep-change in case the underlying source moved
    // between the initial useState evaluation and effect setup. Mirrors what
    // useNodesFromYjs/useEdgesFromYjs did (`refresh()` after observe).
    refresh();
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return snapshot;
}

/**
 * Shallow-copy a Y.Map into a plain object. Nested Y.* values pass through
 * as live refs — callers reading collaborative text/maps should treat the
 * referenced Y types as authoritative, not the snapshot copy.
 */
export function mapToObject(map: Y.Map<unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  map.forEach((value, key) => {
    out[key] = value;
  });
  return out;
}
